import { Fragment, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  getSettledMarkets,
  getSettledBets,
  voidMarketAfterSettlement,
  voidBetAfterSettlement,
  getSuspectAutoVoid,
  saveSuspectAutoVoid,
} from '../../services/api'
import useModalEnter from '../../hooks/useModalEnter'

const LIMIT = 200

// hours = 0 -> all settlements (no time limit)
const WINDOWS = [
  { hours: 24, label: 'last 24h' },
  { hours: 48, label: 'last 48h' },
  { hours: 168, label: 'last 7 days' },
  { hours: 720, label: 'last 30 days' },
  { hours: 0, label: 'all time' },
]

const MARKETS_FOR_FANCY = [
  '1st Innings 6 Overs Line', '2nd Innings 6 Overs Line', '3rd Innings 6 Overs Line',
  '1st Innings 50 Overs Line', '2nd Innings 50 Overs Line', '3rd Innings 50 Overs Line',
  '1st Innings 40 Overs Line', '2nd Innings 40 Overs Line', '3rd Innings 40 Overs Line',
  '1st Innings 30 Overs Line', '2nd Innings 30 Overs Line', '3rd Innings 30 Overs Line',
  '1st Innings 20 Overs Line', '2nd Innings 20 Overs Line', '3rd Innings 20 Overs Line',
  '1st Innings 10 Overs Line', '2nd Innings 10 Overs Line', '3rd Innings 10 Overs Line',
  'Over By Over', 'Ball By Ball', 'Normal', 'khado', 'meter', 'fancy1', 'oddeven',
]

const TABS = [
  { key: 'MATCH_ODDS', label: 'Match Odds' },
  { key: 'BOOKMAKER', label: 'Bookmaker' },
  { key: 'FANCY', label: 'Fancy' },
  { key: 'OTHERS', label: 'Others' },
]

// Server market_type ko waise hi bhejta hai jaise bet me tha — 'MATCH_ODDS',
// 'Bookmaker', 'oddeven'… — isliye compare case-insensitive hona chahiye,
// warna 'Bookmaker' galti se OTHERS me chala jaata hai.
const FANCY_UPPER = MARKETS_FOR_FANCY.map((m) => m.toUpperCase())

// Upar ki list line markets ko OVER COUNT se naam deti hai, isliye team-prefixed
// ("SRI LANKA 2ND INNINGS 95 OVER LINE") aur bina-over-count wale ("... RUNS
// LINE") markets OTHERS tab me chale jaate the. Suffix se match karo — server
// bhi yahi karta hai (server/helper/lineMarket.js).
const isFancyMarketName = (mt) => FANCY_UPPER.includes(mt) || /\bLINE$/.test(mt)

function getTabForMarket(mkt) {
  const mt = String(mkt.marketType || '').toUpperCase()
  if (mt === 'MATCH_ODDS') return 'MATCH_ODDS'
  if (mt === 'BOOKMAKER') return 'BOOKMAKER'
  if (isFancyMarketName(mt)) return 'FANCY'
  return 'OTHERS'
}

function VoidBets() {
  const [activeTab, setActiveTab] = useState('MATCH_ODDS')
  const [markets, setMarkets] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')

  const [expandedMarket, setExpandedMarket] = useState(null)
  const [bets, setBets] = useState([])
  const [betsLoading, setBetsLoading] = useState(false)

  const [confirmModal, setConfirmModal] = useState(null)
  // Confirm dialog open animation + outside click (hooks/useModalEnter)
  const confirmEnter = useModalEnter(() => setConfirmModal(null), !!confirmModal)
  const [txnPassword, setTxnPassword] = useState('')
  // ON (default) = user ke statement me "… -void" wali nayi debit line banegi.
  // OFF = silent void: settlement/commission ki entries statement se hat jaati
  // hain. Paisa dono soorat me ek hi tarah wapas jaata hai.
  const [writeStatement, setWriteStatement] = useState(true)
  const [windowHours, setWindowHours] = useState(24)
  const [suspectOnly, setSuspectOnly] = useState(false)
  // Auto: the results cron voids settled bets with a strong suspect flag (server autoVoidSuspects)
  const [auto, setAuto] = useState(null) // { enabled, mode } once loaded

  useEffect(() => {
    getSuspectAutoVoid().then((r) => { if (r?.success) setAuto(r.data) }).catch(() => {})
  }, [])

  const saveAuto = async (patch) => {
    if (patch.enabled && !window.confirm(
      'Turn Auto void ON?\n\nFrom now on, settled bets of users with a strong suspect flag (VPN / datacenter IP, IP country change, bot or script, '
      + 'opposite side or stake split over the max bet with a same-IP account) are voided automatically, with the reason on the user\'s statement.'
    )) return
    try {
      const r = await saveSuspectAutoVoid(patch)
      if (r?.success) setAuto(r.data)
      else alert('Failed: ' + (r?.error || 'Unknown error'))
    } catch (err) {
      alert('Error: ' + (err?.response?.data?.error || err.message))
    }
  }

  const fetchMarkets = useCallback(async () => {
    setLoading(true)
    try {
      const resp = await getSettledMarkets({ limit: LIMIT, offset: 0, search, hours: windowHours })
      if (resp?.success && Array.isArray(resp.data)) {
        setMarkets(resp.data)
      } else {
        setMarkets([])
      }
    } catch {
      setMarkets([])
    } finally {
      setLoading(false)
    }
  }, [search, windowHours])

  useEffect(() => { fetchMarkets() }, [fetchMarkets])

  const windowLabel = WINDOWS.find((w) => w.hours === windowHours)?.label || `last ${windowHours}h`

  const shownMarkets = suspectOnly ? markets.filter((mkt) => mkt.suspectUsers > 0) : markets
  const filteredMarkets = shownMarkets.filter((mkt) => getTabForMarket(mkt) === activeTab)

  const tabCounts = {}
  TABS.forEach((t) => { tabCounts[t.key] = 0 })
  shownMarkets.forEach((mkt) => { tabCounts[getTabForMarket(mkt)]++ })

  const fetchBets = async (matchId, marketType) => {
    setBetsLoading(true)
    try {
      const resp = await getSettledBets({ match_id: matchId, market_type: marketType, hours: windowHours })
      if (resp?.success && Array.isArray(resp.data)) {
        setBets(resp.data)
      } else {
        setBets([])
      }
    } catch {
      setBets([])
    } finally {
      setBetsLoading(false)
    }
  }

  const isExpanded = (mkt) =>
    expandedMarket?.matchId === mkt.matchId && expandedMarket?.marketType === mkt.marketType

  const handleExpandMarket = (market) => {
    if (isExpanded(market)) {
      setExpandedMarket(null)
      setBets([])
    } else {
      setExpandedMarket({ matchId: market.matchId, marketType: market.marketType })
      fetchBets(market.matchId, market.marketType)
    }
  }

  const [reason, setReason] = useState('')          // shown to the player on the -void statement line
  const [openBets, setOpenBets] = useState({})      // ledgerId -> true: MO/BM row expanded into its bets
  const handleVoidMarket = (market) => { setTxnPassword(''); setReason(''); setWriteStatement(true); setConfirmModal({ type: 'market', data: market, loading: false }) }
  const handleVoidBet = (bet) => { setTxnPassword(''); setReason(''); setWriteStatement(true); setConfirmModal({ type: 'bet', data: bet, loading: false }) }
  // one bet out of a settled MO/BM row: only its own share is reversed (server voidSettledBet)
  const handleVoidOne = (row, b) => { setTxnPassword(''); setReason(''); setWriteStatement(true); setConfirmModal({ type: 'single', data: { ...b, ledgerId: row.ledgerId, username: row.username, userId: row.userId, netamount: b.pnl }, loading: false }) }

  const confirmVoid = async () => {
    if (!confirmModal) return
    setConfirmModal((prev) => ({ ...prev, loading: true }))
    try {
      if (confirmModal.type === 'market') {
        const resp = await voidMarketAfterSettlement({
          match_id: confirmModal.data.matchId,
          market_type: confirmModal.data.marketType,
          write_statement: writeStatement,
          reason: reason.trim() || undefined,
          hours: windowHours,
          transactionPassword: txnPassword,
        })
        if (resp?.success) {
          alert('Market voided successfully. Affected users: ' + (resp.affectedUsers || 0))
        } else {
          alert('Failed: ' + (resp?.error || 'Unknown error'))
        }
      } else {
        const resp = await voidBetAfterSettlement({
          ledger_id: confirmModal.data.ledgerId,
          ...(confirmModal.type === 'single' ? { bet_id: confirmModal.data.betId } : {}),
          write_statement: confirmModal.type === 'single' ? true : writeStatement,
          reason: reason.trim() || undefined,
          hours: windowHours,
          transactionPassword: txnPassword,
        })
        if (resp?.success) {
          alert((confirmModal.type === 'single' ? `Bet #${confirmModal.data.betId} voided` : 'Bet voided successfully') + ' for user: ' + (confirmModal.data.username || confirmModal.data.userId))
        } else {
          alert('Failed: ' + (resp?.error || 'Unknown error'))
        }
      }
    } catch (err) {
      alert('Error: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
    } finally {
      setConfirmModal(null)
      fetchMarkets()
      if (expandedMarket) {
        fetchBets(expandedMarket.matchId, expandedMarket.marketType)
      }
    }
  }

  const betTypeBadge = (bt) => {
    if (!bt) return <span className="text-muted">-</span>
    const isBack = bt === 'back' || bt === 'yes' || bt === 'Yes'
    return <span className={`badge ${isBack ? 'badge-primary' : 'badge-danger'} px-2`}>{bt.toUpperCase()}</span>
  }

  const resultBadge = (status) => {
    if (!status) return <span className="text-muted">-</span>
    const cls = status === 'won' ? 'badge-success' : status === 'loss' ? 'badge-danger' : 'badge-secondary'
    return <span className={`badge ${cls} px-2`}>{status.toUpperCase()}</span>
  }

  const netClass = (v) => (v >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold')

  // IPs come with country · ISP from the server; red = VPN / proxy / datacenter
  const ipCell = (list) => (!list?.length ? <span className="text-muted">-</span> : list.map((i) => (
    <div key={i.ip} className={i.vpn ? 'text-danger font-weight-bold' : ''}>
      {i.ip}{i.info && <small className="d-block text-muted">{i.info}</small>}
    </div>
  )))

  // strong = reasons Auto voids on; shown bold with an AUTO tag
  const suspectCell = (reasons, strong = []) => (!reasons?.length ? <span className="text-muted">-</span> : (
    <>
      <span className="badge badge-danger px-2">SUSPECT</span>
      {reasons.map((r) => (
        <small key={r} className={`d-block text-danger ${strong.includes(r) ? 'font-weight-bold' : ''}`}>
          {strong.includes(r) && <span className="badge badge-dark mr-1">AUTO</span>}{r}
        </small>
      ))}
    </>
  ))

  return (
    <div>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">Void Bets (After Settlement) — {windowLabel}</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><Link to="/admin/home">Home</Link></li>
                <li className="breadcrumb-item active"><span aria-current="location">Void Bets</span></li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-8">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by match ID, market type, team name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchMarkets() }}
                />
                <div className="input-group-append">
                  <button className="btn btn-primary" onClick={fetchMarkets}>Search</button>
                </div>
              </div>
            </div>
            <div className="col-md-4 text-md-right mt-2 mt-md-0">
              <label className="mb-0 mr-2 align-middle">
                <input type="checkbox" className="mr-1" checked={suspectOnly} onChange={(e) => setSuspectOnly(e.target.checked)} />
                Suspect only
              </label>
              <select
                className="form-control form-control-sm d-inline-block w-auto mr-2"
                aria-label="Settlement window"
                value={windowHours}
                onChange={(e) => { setWindowHours(Number(e.target.value)); setExpandedMarket(null); setBets([]) }}
              >
                {WINDOWS.map((w) => <option key={w.hours} value={w.hours}>{w.label}</option>)}
              </select>
              <button className="btn btn-primary btn-sm" onClick={fetchMarkets} disabled={loading}>
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>

          {auto && (
            <div className={`alert ${auto.enabled ? 'alert-danger' : 'alert-secondary'} d-flex flex-wrap align-items-center py-2`}>
              <div className="custom-control custom-switch mr-3">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="suspect-auto-void"
                  checked={auto.enabled}
                  onChange={(e) => saveAuto({ enabled: e.target.checked })}
                />
                <label className="custom-control-label font-weight-bold" htmlFor="suspect-auto-void">
                  Auto void suspects: {auto.enabled ? 'ON' : 'OFF'}
                </label>
              </div>
              <select
                className="form-control form-control-sm w-auto mr-3"
                aria-label="Auto void which bets"
                value={auto.mode}
                onChange={(e) => saveAuto({ mode: e.target.value })}
              >
                <option value="winning">Winning bets only</option>
                <option value="all">All suspect bets</option>
              </select>
              <small className="text-muted">
                Voids on AUTO reasons only (VPN / datacenter IP, IP country change, bot or script, opposite side or stake split over the max bet with a same-IP account), for bets settled after it was turned on.
              </small>
            </div>
          )}

          <ul className="nav nav-tabs mb-3">
            {TABS.map((tab) => (
              <li className="nav-item" key={tab.key}>
                <a
                  href="javascript:void(0)"
                  className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); setActiveTab(tab.key); setExpandedMarket(null); setBets([]) }}
                >
                  {tab.label} ({tabCounts[tab.key] || 0})
                </a>
              </li>
            ))}
          </ul>

          <div className="table-responsive">
            <table className="table table-bordered mb-0">
              <thead>
                <tr>
                  <th>Match Title</th>
                  <th>Market Type</th>
                  <th className="text-right">Entries</th>
                  <th className="text-right">Users</th>
                  <th className="text-right">Net Amount</th>
                  <th className="text-center" style={{ minWidth: 220 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center">Loading...</td></tr>
                ) : filteredMarkets.length === 0 ? (
                  <tr><td colSpan={6} className="text-center">No markets settled in {windowLabel}</td></tr>
                ) : (
                  filteredMarkets.map((mkt, idx) => (
                    <Fragment key={`${mkt.matchId}-${mkt.marketType}-${idx}`}>
                      <tr>
                        <td>
                          <div className="font-weight-bold">
                            {mkt.matchTitle || (mkt.teamOne && mkt.teamTwo ? `${mkt.teamOne} vs ${mkt.teamTwo}` : mkt.matchId)}
                          </div>
                          <small className="text-muted">{mkt.matchId}</small>
                          {mkt.suspectUsers > 0 && <span className="badge badge-danger ml-2">{mkt.suspectUsers} suspect</span>}
                        </td>
                        <td>{mkt.marketType || '-'}</td>
                        <td className="text-right">{mkt.totalEntries}</td>
                        <td className="text-right">{mkt.totalUsers}</td>
                        <td className="text-right">
                          <span className={netClass(mkt.totalNetAmount)}>
                            {mkt.totalNetAmount >= 0 ? '+' : ''}{Number(mkt.totalNetAmount || 0).toFixed(2)}
                          </span>
                        </td>
                        <td className="text-center">
                          <button onClick={() => handleExpandMarket(mkt)} className="btn btn-primary btn-sm mr-1">
                            {isExpanded(mkt) ? 'Hide Bets' : 'View Bets'}
                          </button>
                          <button onClick={() => handleVoidMarket(mkt)} className="btn btn-danger btn-sm">
                            Void Market
                          </button>
                        </td>
                      </tr>
                      {isExpanded(mkt) && (
                        <tr>
                          <td colSpan={6} className="bg-light p-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong>Individual Bets — {mkt.marketType}</strong>
                              <small className="text-muted">{bets.length} bet{bets.length !== 1 ? 's' : ''}</small>
                            </div>
                            {betsLoading ? (
                              <div className="text-center text-muted py-3">Loading bets...</div>
                            ) : bets.length === 0 ? (
                              <div className="text-center text-muted py-3">No bets found for this market</div>
                            ) : (
                              <div className="table-responsive">
                                <table className="table table-sm table-bordered mb-0">
                                  <thead>
                                    <tr>
                                      <th>Bet ID</th>
                                      <th>User</th>
                                      <th>Selection</th>
                                      <th className="text-center">Bet Type</th>
                                      <th className="text-right">Odds</th>
                                      <th className="text-right">Stake</th>
                                      <th className="text-right">Net Amount</th>
                                      <th className="text-center">Result</th>
                                      <th>IP</th>
                                      <th>Suspect</th>
                                      <th className="text-center">Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {bets.map((bet) => (
                                      <Fragment key={bet.ledgerId}>
                                      <tr>
                                        <td>#{bet.betId}</td>
                                        <td>{bet.username}</td>
                                        <td>
                                          {Array.isArray(bet.bets) && bet.bets.length ? (
                                            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setOpenBets((o) => ({ ...o, [bet.ledgerId]: !o[bet.ledgerId] }))}>
                                              {openBets[bet.ledgerId] ? '▾' : '▸'} {bet.bets.length} bets
                                            </button>
                                          ) : (bet.selectionName || '-')}
                                        </td>
                                        <td className="text-center">{betTypeBadge(bet.betType)}</td>
                                        <td className="text-right">{bet.odds ?? '-'}</td>
                                        <td className="text-right">{bet.stakeAmount != null ? Number(bet.stakeAmount).toFixed(2) : '-'}</td>
                                        <td className="text-right">
                                          <span className={netClass(bet.netamount)}>
                                            {bet.netamount >= 0 ? '+' : ''}{Number(bet.netamount || 0).toFixed(2)}
                                          </span>
                                        </td>
                                        <td className="text-center">{resultBadge(bet.resultStatus)}</td>
                                        <td>{ipCell(bet.ips)}</td>
                                        <td style={{ minWidth: 220 }}>{suspectCell(bet.suspectReasons, bet.suspectStrong)}</td>
                                        <td className="text-center">
                                          <button onClick={() => handleVoidBet(bet)} className="btn btn-warning btn-sm">Void Bet</button>
                                        </td>
                                      </tr>
                                      {openBets[bet.ledgerId] && bet.bets.map((b) => (
                                        <tr key={`${bet.ledgerId}-${b.betId}`} className="table-light">
                                          <td className="pl-4">#{b.betId}</td>
                                          <td className="text-muted">{bet.username}</td>
                                          <td>{b.selectionName}</td>
                                          <td className="text-center">{betTypeBadge(b.betType)}</td>
                                          <td className="text-right">{b.odds ?? '-'}</td>
                                          <td className="text-right">{Number(b.stakeAmount || 0).toFixed(2)}</td>
                                          <td className="text-right"><span className={netClass(b.pnl)}>{b.pnl >= 0 ? '+' : ''}{Number(b.pnl || 0).toFixed(2)}</span></td>
                                          <td className="text-center">{resultBadge(b.pnl > 0 ? 'won' : b.pnl < 0 ? 'loss' : 'settled')}</td>
                                          <td>{ipCell(b.ip?.ip ? [b.ip] : [])}</td>
                                          <td />
                                          <td className="text-center">
                                            <button onClick={() => handleVoidOne(bet, b)} className="btn btn-outline-warning btn-sm">Void this bet</button>
                                          </td>
                                        </tr>
                                      ))}
                                      </Fragment>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {confirmModal && (
        <div style={{ position: 'absolute', zIndex: 1040 }}>
          <div role="dialog" className={confirmEnter.modalClass} aria-modal="true" style={{ display: 'block' }} onClick={confirmEnter.onBackdropClick}>
            <div className="modal-dialog modal-md">
              <div className="modal-content">
                <header className="modal-header">
                  <h5 className="modal-title">
                    {confirmModal.type === 'market' ? 'Void Entire Market' : confirmModal.type === 'single' ? 'Void One Bet (of a settled market)' : 'Void Single Bet'}
                  </h5>
                  <button type="button" className="close" onClick={() => setConfirmModal(null)}>×</button>
                </header>
                <div className="modal-body">
                  <div className="alert alert-warning mb-0">
                    {confirmModal.type === 'market' ? (
                      <>
                        <p className="mb-2">
                          Void <strong>all bets</strong> in market <strong>{confirmModal.data.marketType}</strong> for match{' '}
                          <strong>{confirmModal.data.matchTitle || confirmModal.data.matchId}</strong>?
                        </p>
                        <p className="mb-0 text-muted">
                          This will reverse settled amounts for <strong>{confirmModal.data.totalEntries}</strong> entries across{' '}
                          <strong>{confirmModal.data.totalUsers}</strong> users. This action cannot be undone.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="mb-2">
                          Void bet <strong>#{confirmModal.data.betId}</strong> for user <strong>{confirmModal.data.username}</strong>?
                        </p>
                        <p className="mb-0 text-muted">
                          Net amount to reverse:{' '}
                          <span className={netClass(confirmModal.data.netamount)}>
                            {confirmModal.data.netamount >= 0 ? '+' : ''}{Number(confirmModal.data.netamount || 0).toFixed(2)}
                          </span>
                          . This action cannot be undone.
                        </p>
                      </>
                    )}
                  </div>
                  {confirmModal.type === 'single' && (
                    <div className="alert alert-info mt-3 mb-0 py-2">
                      Only this bet&apos;s own share ({confirmModal.data.selectionName}, {String(confirmModal.data.betType || '').toUpperCase()} @ {confirmModal.data.odds}) is reversed.
                      The user&apos;s other bets in this market stay settled. A <strong>-void</strong> line is always written.
                    </div>
                  )}
                  <div className="form-group mt-3 mb-0">
                    <label>Reason <small className="text-muted">(optional — the user sees it on the -void line)</small></label>
                    <input
                      type="text"
                      className="form-control"
                      maxLength={200}
                      value={reason}
                      placeholder="e.g. wrong odds / bet after result"
                      onChange={(e) => setReason(e.target.value)}
                    />
                  </div>
                  {confirmModal.type !== 'single' && (<>
                  <div className="custom-control custom-checkbox mt-3">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="voidWriteStatement"
                      checked={writeStatement}
                      onChange={(e) => setWriteStatement(e.target.checked)}
                    />
                    <label className="custom-control-label" htmlFor="voidWriteStatement">
                      Create statement entry — a <strong>-void</strong> line in the user&apos;s account statement
                    </label>
                  </div>
                  {!writeStatement && (
                    <div className="alert alert-danger mt-2 mb-0 py-2">
                      <strong>Silent void.</strong> The settlement{' '}
                      {confirmModal.type === 'market' ? 'and commission entries' : 'and commission entry'} will be{' '}
                      <strong>deleted</strong> from the account statement — nothing will show there.
                      The money is refunded either way.
                    </div>
                  )}
                  </>)}

                  <div className="form-group mt-3 mb-0">
                    <label>Transaction Password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={txnPassword}
                      autoComplete="off"
                      onChange={(e) => setTxnPassword(e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setConfirmModal(null)}>Cancel</button>
                  <button className="btn btn-danger" onClick={confirmVoid} disabled={confirmModal.loading || !txnPassword}>
                    {confirmModal.loading ? 'Processing...' : 'Confirm Void'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className={confirmEnter.backdropClass}></div>
        </div>
      )}
    </div>
  )
}

export default VoidBets
